// frontend/src/pages/PetSuppliersPage.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import useApi from '../hooks/useApi';
import { 
    Search, Edit, Trash2, X, User, ChevronDown, ChevronUp, UserPlus, 
    Info, Camera, DollarSign, Settings, Loader2, MapPin, Mail, 
    Phone, AlertTriangle, Table
} from 'lucide-react';

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
};

const ImageUpload = ({ currentImagePath, placeholderIcon, onFileSelect }) => {
    const [preview, setPreview] = useState(null);
    const fileInputRef = useRef(null);
    const [hasError, setHasError] = useState(false);

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setPreview(URL.createObjectURL(file));
            onFileSelect(file);
            setHasError(false);
        }
    };

    useEffect(() => {
        setPreview(null);
        setHasError(false);
    }, [currentImagePath]);

    const imageUrl = preview || (currentImagePath ? `/api/uploads/pet_images/${currentImagePath}?t=${new Date().getTime()}` : null);

    return (
        <div className="relative w-24 h-24">
            {imageUrl && !hasError ? (
                <img src={imageUrl} alt="Perfil" className="w-24 h-24 rounded-full object-cover border-2 border-gray-200" onError={() => setHasError(true)} />
            ) : (
                <div className="w-24 h-24 rounded-full bg-gray-100 flex items-center justify-center border-2 border-gray-200">
                    {placeholderIcon}
                </div>
            )}
            <input type="file" accept="image/png, image/jpeg" ref={fileInputRef} onChange={handleFileChange} className="hidden" />
            <button type="button" onClick={() => fileInputRef.current.click()} className="absolute bottom-0 right-0 p-1.5 bg-blue-600 text-white rounded-full hover:bg-blue-700 ring-2 ring-white shadow-sm">
                <Camera size={14} />
            </button>
        </div>
    );
};

const SupplierFormModal = ({ supplierToEdit, onFinish, onDataChange }) => {
    const isEditing = !!supplierToEdit;
    const [activeTab, setActiveTab] = useState('info'); 
    const { apiFetch } = useApi(); 
    
    const formatCPF = (value) => {
        if (!value) return '';
        const onlyNums = value.replace(/[^\d]/g, '');
        return onlyNums.replace(/(\d{3})(\d)/, '$1.$2').replace(/(\d{3})\.(\d{3})(\d)/, '$1.$2.$3').replace(/(\d{3})\.(\d{3})\.(\d{3})(\d{1,2})/, '$1.$2.$3-$4').substring(0, 14);
    };
    
    const formatCNPJ = (value) => {
        if (!value) return '';
        const onlyNums = value.replace(/[^\d]/g, '');
        return onlyNums.replace(/(\d{2})(\d)/, '$1.$2').replace(/(\d{2})\.(\d{3})(\d)/, '$1.$2.$3').replace(/(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4').replace(/(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d{1,2})/, '$1.$2.$3/$4-$5').substring(0, 18);
    };

    const [supplier, setSupplier] = useState(supplierToEdit || {
        name: '', email: '', phone: '', document_type: 'CNPJ', document_id: '', inscricao_estadual: '',
        address_street: '', address_number: '', address_complement: '', address_city: '', address_state: '', address_zip: '',
        billing_contact_name: '', billing_email: '', billing_phone: '', billing_notes: '', config_link: ''
    });
    const [photoFile, setPhotoFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setSupplier(prev => ({ ...prev, [name]: value }));
    };

    const handleDocumentTypeChange = (e) => {
        const newType = e.target.value;
        setSupplier(prev => ({ ...prev, document_type: newType, document_id: '', inscricao_estadual: '' }));
    };

    const handleDocumentIdChange = (e) => {
        let value = e.target.value;
        if (supplier.document_type === 'CPF') value = formatCPF(value);
        else if (supplier.document_type === 'CNPJ') value = formatCNPJ(value);
        else value = value.substring(0, 30);
        setSupplier(prev => ({ ...prev, document_id: value }));
    };
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            let res;
            if (isEditing) {
                await apiFetch(`/pet-suppliers/${supplier.id}`, { method: 'PUT', body: supplier });
                res = supplier;
            } else {
                const data = await apiFetch('/pet-suppliers', { method: 'POST', body: supplier });
                res = { ...supplier, id: data.id };
            }

            if (photoFile) {
                const fd = new FormData();
                fd.append('clientPhoto', photoFile);
                await apiFetch(`/pet-suppliers/${res.id}/upload-photo`, { method: 'POST', body: fd });
            }

            onDataChange();
            onFinish();
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };
    
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between p-4 border-b">
                    <h2 className="text-xl font-bold">{isEditing ? `Editar: ${supplier.name}` : 'Novo Fornecedor Pet'}</h2>
                    <button onClick={onFinish} className="p-2 rounded-full hover:bg-gray-200"><X /></button>
                </div>
                
                <div className="flex border-b overflow-x-auto">
                    <button onClick={() => setActiveTab('info')} className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'info' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}><Info size={16}/> Informações Gerais</button>
                    <button onClick={() => setActiveTab('billing')} className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'billing' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}><DollarSign size={16}/> Dados Financeiros</button>
                    <button onClick={() => setActiveTab('config')} className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'config' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500'}`}><Settings size={16}/> Configurações</button>
                </div>

                <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
                    <div className="flex-1 overflow-y-auto p-6 space-y-6">
                        {activeTab === 'info' && (
                            <div className="flex flex-col md:flex-row gap-8">
                                <div className="flex-shrink-0">
                                    <ImageUpload currentImagePath={supplier.profile_picture_path} placeholderIcon={<User size={40} className="text-gray-400" />} onFileSelect={setPhotoFile} />
                                </div>
                                <div className="flex-grow space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div className="md:col-span-2">
                                            <label className="text-sm font-medium text-gray-700">Nome / Razão Social</label>
                                            <input type="text" name="name" value={supplier.name} onChange={handleChange} required className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" />
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Tipo de Documento</label>
                                            <select name="document_type" value={supplier.document_type} onChange={handleDocumentTypeChange} className="w-full p-2 mt-1 border rounded">
                                                <option value="CNPJ">CNPJ</option>
                                                <option value="CPF">CPF</option>
                                                <option value="ID Global">ID Global (Passaporte e etc)</option>
                                            </select>
                                        </div>
                                        <div>
                                            <label className="text-sm font-medium text-gray-700">Documento ID</label>
                                            <input 
                                                type="text" 
                                                name="document_id" 
                                                value={supplier.document_id} 
                                                onChange={handleDocumentIdChange} 
                                                className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" 
                                                placeholder={supplier.document_type === 'ID Global' ? "Digite letras e números" : "Número do documento"}
                                            />
                                        </div>
                                        {supplier.document_type === 'CNPJ' && (
                                            <div className="md:col-span-2">
                                                <label className="text-sm font-medium text-gray-700">Inscrição Estadual</label>
                                                <input type="text" name="inscricao_estadual" value={supplier.inscricao_estadual} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="pt-4 border-t space-y-4">
                                        <h3 className="text-lg font-semibold flex items-center gap-2 text-gray-800"><MapPin size={18} className="text-blue-600" /> Endereço</h3>
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-2">
                                            <div className="md:col-span-2">
                                                <label className="text-sm font-medium text-gray-700">Rua/Avenida</label>
                                                <input type="text" name="address_street" value={supplier.address_street || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Nº</label>
                                                <input type="text" name="address_number" value={supplier.address_number || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" />
                                            </div>
                                            <div className="md:col-span-3">
                                                <label className="text-sm font-medium text-gray-700">Complemento</label>
                                                <input type="text" name="address_complement" value={supplier.address_complement || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Cidade</label>
                                                <input type="text" name="address_city" value={supplier.address_city || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">Estado</label>
                                                <input type="text" name="address_state" value={supplier.address_state || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" />
                                            </div>
                                            <div>
                                                <label className="text-sm font-medium text-gray-700">CEP</label>
                                                <input type="text" name="address_zip" value={supplier.address_zip || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {activeTab === 'billing' && (
                            <div className="space-y-4 max-w-lg">
                                <div><label className="text-sm font-medium text-gray-700">Contato Financeiro</label><input type="text" name="billing_contact_name" value={supplier.billing_contact_name} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" /></div>
                                <div><label className="text-sm font-medium text-gray-700">Email Financeiro (NF)</label><input type="email" name="billing_email" value={supplier.billing_email} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" /></div>
                                <div><label className="text-sm font-medium text-gray-700">Telefone Financeiro</label><input type="tel" name="billing_phone" value={supplier.billing_phone} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" /></div>
                                <div><label className="text-sm font-medium text-gray-700">Observações de Faturamento</label><textarea name="billing_notes" value={supplier.billing_notes} onChange={handleChange} className="w-full p-2 mt-1 border rounded h-32 resize-none focus:ring-2 focus:ring-blue-500" /></div>
                            </div>
                        )}
                        {activeTab === 'config' && (
                            <div className="space-y-4 max-w-lg">
                                <div>
                                    <label className="text-sm font-medium text-gray-700">Link de Configuração</label>
                                    <input type="url" name="config_link" value={supplier.config_link || ''} onChange={handleChange} className="w-full p-2 mt-1 border rounded focus:ring-2 focus:ring-blue-500" placeholder="https://exemplo.com/config" />
                                    <p className="text-xs text-gray-500 mt-1">Link externo para acesso a configurações ou portal do fornecedor.</p>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="p-4 bg-gray-50 border-t flex justify-end items-center">
                        {error && <span className="text-red-600 mr-4 text-sm font-medium">{error}</span>}
                        <button type="button" onClick={onFinish} className="px-6 py-2 mr-2 font-semibold bg-gray-200 rounded-md hover:bg-gray-300">Cancelar</button>
                        <button type="submit" disabled={loading} className="px-6 py-2 font-semibold text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400">
                            {loading ? <Loader2 className="animate-spin" size={18}/> : 'Salvar Fornecedor'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

const PetSuppliersPage = ({ onDataChange }) => {
    const { apiFetch, permissions } = useApi();
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const debouncedSearch = useDebounce(searchTerm, 300);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSupplier, setEditingSupplier] = useState(null);
    const [expandedId, setExpandedId] = useState(null);

    const fetchSuppliers = useCallback(async (search = '') => {
        setLoading(true);
        try {
            const data = await apiFetch(`/pet-suppliers?search=${search}`);
            setSuppliers(data || []);
        } catch (err) {
            console.error("Erro ao carregar fornecedores:", err);
        } finally {
            setLoading(false);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchSuppliers(debouncedSearch);
    }, [fetchSuppliers, debouncedSearch]);

    const handleDelete = async (id) => {
        if (window.confirm("Tem certeza que deseja excluir este fornecedor? Esta ação não pode ser desfeita.")) {
            try {
                await apiFetch(`/pet-suppliers/${id}`, { method: 'DELETE' });
                fetchSuppliers(debouncedSearch);
                onDataChange();
            } catch (err) {
                alert("Erro ao excluir: " + err.message);
            }
        }
    };

    return (
        <div className="max-w-6xl mx-auto">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-gray-800 tracking-tight">Fornecedores Pet</h1>
                    <p className="text-gray-500">Gestão centralizada de fornecedores e parceiros da clínica.</p>
                </div>
                <button onClick={() => { setEditingSupplier(null); setIsModalOpen(true); }} className="flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white font-bold rounded-lg hover:bg-green-700 shadow-md transition-all">
                    <UserPlus size={20} /> Novo Fornecedor
                </button>
            </div>

            <div className="mb-8 relative group">
                <input 
                    type="text" 
                    value={searchTerm} 
                    onChange={e => setSearchTerm(e.target.value)} 
                    placeholder="Pesquisar por nome, número ou documento..." 
                    className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all" 
                />
                <Search className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-blue-500 transition-colors" size={22} />
            </div>

            {loading ? (
                <div className="flex flex-col items-center justify-center p-20 text-gray-400">
                    <Loader2 className="animate-spin text-blue-600 mb-4" size={40} />
                    <p className="font-medium animate-pulse">Buscando fornecedores...</p>
                </div>
            ) : (
                <div className="space-y-4">
                    {suppliers.map(s => (
                        <div key={s.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                            <div className="flex items-center justify-between p-5 cursor-pointer" onClick={() => setExpandedId(expandedId === s.id ? null : s.id)}>
                                <div className="flex items-center gap-5">
                                    <div className="w-14 h-14 bg-blue-50 rounded-full flex items-center justify-center overflow-hidden border border-blue-100">
                                        {s.profile_picture_path ? (
                                            <img src={`/api/uploads/pet_images/${s.profile_picture_path}`} className="w-full h-full object-cover" alt="" />
                                        ) : (
                                            <User className="text-blue-300" size={28} />
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                                            {s.name} 
                                            <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-500 rounded font-mono">#{s.supplier_number}</span>
                                        </h3>
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 mt-1">
                                            <span className="flex items-center gap-1.5 font-medium"><Phone size={15} className="text-blue-500"/> {s.billing_phone || 'Sem Telefone Financeiro'}</span>
                                            <span className="flex items-center gap-1.5 font-medium"><Mail size={15} className="text-blue-500"/> {s.billing_email || 'Sem E-mail Financeiro'}</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button onClick={(e) => { e.stopPropagation(); setEditingSupplier(s); setIsModalOpen(true); }} className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"><Edit size={20}/></button>
                                    <button onClick={(e) => { e.stopPropagation(); handleDelete(s.id); }} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 size={20}/></button>
                                    <div className="ml-2 text-gray-300">{expandedId === s.id ? <ChevronUp /> : <ChevronDown />}</div>
                                </div>
                            </div>
                            
                            {expandedId === s.id && (
                                <div className="p-6 bg-gray-50 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2 duration-200">
                                    <div className="space-y-3">
                                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Localização e Registro</h4>
                                        <p className="text-sm flex items-center gap-2 text-gray-700"><MapPin size={16} className="text-gray-400"/> {s.address_street || 'Rua não cadastrada'}, {s.address_number || 'S/N'} - {s.address_city || 'Cidade'}</p>
                                        <p className="text-sm flex items-center gap-2 text-gray-700"><Table size={16} className="text-gray-400"/> {s.document_type || 'DOC'}: {s.document_id || 'Não informado'}</p>
                                    </div>
                                    <div className="space-y-3">
                                        <h4 className="text-xs font-bold text-gray-400 uppercase tracking-widest">Informações Financeiras</h4>
                                        <p className="text-sm text-gray-700"><strong>Contato Principal:</strong> {s.billing_contact_name || 'Não informado'}</p>
                                        <p className="text-sm text-gray-700"><strong>Email NF:</strong> {s.billing_email || 'N/A'}</p>
                                        <p className="text-sm text-gray-700"><strong>Notas Internas:</strong> {s.billing_notes || '-'}</p>
                                        {s.config_link && (
                                            <p className="text-sm text-gray-700"><strong>Link Config:</strong> <a href={s.config_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{s.config_link}</a></p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                    {suppliers.length === 0 && (
                        <div className="text-center p-16 bg-white rounded-2xl border-2 border-dashed border-gray-100 flex flex-col items-center">
                            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mb-4"><Search className="text-gray-300" size={30}/></div>
                            <p className="text-gray-500 font-medium">Nenhum fornecedor encontrado.</p>
                            <p className="text-sm text-gray-400">Tente ajustar sua busca ou cadastrar um novo parceiro.</p>
                        </div>
                    )}
                </div>
            )}

            {isModalOpen && <SupplierFormModal supplierToEdit={editingSupplier} onFinish={() => setIsModalOpen(false)} onDataChange={() => fetchSuppliers(debouncedSearch)} />}
        </div>
    );
};

export default PetSuppliersPage;